import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  architectureDecisionRecordsApi,
  type ADRRecord,
  type ADRCategory,
  type CrossReference,
  type DecisionIndexEntry,
  type Deliverable,
  type DependencyLink,
  type EngineeringGuideline,
  type GovernanceRule,
  type MandatoryDecision,
  type RepositoryDashboard,
  type RepositoryValidation,
  type ADRMasterStatus,
  type TemplateField,
} from '../../api/architecture-decision-records';

type Tab = 'overview' | 'categories' | 'template' | 'mandatory' | 'records' | 'index' | 'dependencies' | 'governance' | 'status';
type Theme = 'light' | 'dark';

const statusColors: Record<string, string> = {
  accepted: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
  proposed: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  superseded: 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  registered: 'bg-stone-200 text-stone-700 dark:bg-stone-700 dark:text-stone-200',
  pending: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
};

export function ArchitectureDecisionRecordsPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<RepositoryDashboard | null>(null);
  const [categories, setCategories] = useState<ADRCategory[]>([]);
  const [template, setTemplate] = useState<TemplateField[]>([]);
  const [mandatory, setMandatory] = useState<MandatoryDecision[]>([]);
  const [records, setRecords] = useState<ADRRecord[]>([]);
  const [index, setIndex] = useState<DecisionIndexEntry[]>([]);
  const [dependencies, setDependencies] = useState<DependencyLink[]>([]);
  const [crossRefs, setCrossRefs] = useState<CrossReference[]>([]);
  const [governance, setGovernance] = useState<GovernanceRule[]>([]);
  const [guidelines, setGuidelines] = useState<EngineeringGuideline[]>([]);
  const [deliverables, setDeliverables] = useState<Deliverable[]>([]);
  const [status, setStatus] = useState<ADRMasterStatus | null>(null);
  const [validation, setValidation] = useState<RepositoryValidation | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<ADRRecord | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, cat, tmpl, mand, rec, idx, deps, refs, gov, guide, del, st] = await Promise.all([
        architectureDecisionRecordsApi.dashboard(),
        architectureDecisionRecordsApi.categories(),
        architectureDecisionRecordsApi.template(),
        architectureDecisionRecordsApi.mandatoryDecisions(),
        architectureDecisionRecordsApi.records(),
        architectureDecisionRecordsApi.decisionIndex(),
        architectureDecisionRecordsApi.dependencyMap(),
        architectureDecisionRecordsApi.crossReferences(),
        architectureDecisionRecordsApi.governanceRules(),
        architectureDecisionRecordsApi.engineeringGuidelines(),
        architectureDecisionRecordsApi.deliverables(),
        architectureDecisionRecordsApi.status(),
      ]);
      setDashboard(dash);
      setCategories(cat);
      setTemplate(tmpl);
      setMandatory(mand);
      setRecords(rec);
      setIndex(idx);
      setDependencies(deps);
      setCrossRefs(refs);
      setGovernance(gov);
      setGuidelines(guide);
      setDeliverables(del);
      setStatus(st);
      if (rec.length > 0 && !selectedRecord) setSelectedRecord(rec[0]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load ADR master repository');
    }
  }, [selectedRecord]);

  useEffect(() => { refresh(); }, [refresh]);

  const validateRepository = async () => {
    setBusy(true);
    setActionMsg(null);
    try {
      const result = await architectureDecisionRecordsApi.validate({
        categories_defined: true,
        template_complete: true,
        mandatory_adrs_registered: true,
        governance_rules_defined: true,
        deliverables_complete: true,
        records_seeded: true,
        acceptance_passed: true,
      });
      setValidation(result);
      setActionMsg(result.valid ? 'ADR master repository passes all acceptance criteria' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Validation failed');
    } finally {
      setBusy(false);
    }
  };

  const card = dark ? 'border-stone-800/50 bg-slate-900' : 'border-stone-200 bg-white';
  const muted = dark ? 'text-stone-400' : 'text-stone-500';
  const shell = dark ? 'bg-slate-950 text-stone-100' : 'bg-stone-50/40 text-stone-900';
  const accent = dark ? 'text-stone-300' : 'text-stone-700';
  const tabActive = 'bg-stone-700 text-white dark:bg-stone-500';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'categories', label: 'Categories' },
    { id: 'template', label: 'Template' },
    { id: 'mandatory', label: 'Mandatory ADRs' },
    { id: 'records', label: 'Decision Records' },
    { id: 'index', label: 'Decision Index' },
    { id: 'dependencies', label: 'Dependency Map' },
    { id: 'governance', label: 'Governance' },
    { id: 'status', label: 'Status' },
  ];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>ADR-000001</p>
          <h1 className="text-2xl font-semibold">Architecture Decision Records</h1>
          <p className={`text-sm ${muted}`}>
            Immutable technical decision register, engineering rationale, trade-off analysis and long-term architectural governance
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/architecture-governance" className={`${accent} hover:underline`}>Architecture Governance</Link>
            <span className={muted}>→</span>
            <Link to="/platform/engineering-execution-bible" className={`${accent} hover:underline`}>EEB-000001</Link>
          </div>
        </div>
        <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>
          {dark ? 'Light' : 'Dark'}
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? tabActive : `${card} border`}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className="text-lg font-semibold">Engineering Memory</h2>
            <p className={`mt-1 text-sm ${muted}`}>Applies to: {dashboard.applies_to.join(' · ')}</p>
            <p className={`mt-3 text-sm ${muted}`}>
              Every significant technical, architectural, product and engineering decision SHALL be documented before implementation.
              No major architectural change SHALL occur without an ADR. ADRs are immutable historical records — changes create superseding ADRs.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Categories', value: dashboard.categories },
              { label: 'Template Fields', value: dashboard.template_fields },
              { label: 'Mandatory Decisions', value: dashboard.mandatory_decisions },
              { label: 'ADR Records', value: dashboard.adr_records },
              { label: 'Decision Index', value: dashboard.decision_index },
              { label: 'Dependency Links', value: dashboard.dependency_links },
              { label: 'Cross References', value: dashboard.cross_references },
              { label: 'Governance Rules', value: dashboard.governance_rules },
            ].map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
                <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
                <p className="mt-1 text-2xl font-semibold">{m.value}</p>
              </div>
            ))}
          </div>
          <div className={`rounded-xl border p-5 ${card}`}>
            <h3 className="font-semibold">Engineering Guidelines</h3>
            <ul className="mt-3 space-y-2">
              {guidelines.map((g) => (
                <li key={g.key} className="flex items-start gap-2 text-sm">
                  <span className={`mt-0.5 rounded px-1.5 py-0.5 text-xs ${g.mandatory ? 'bg-stone-700 text-white dark:bg-stone-500' : 'bg-stone-200 text-stone-600 dark:bg-stone-800 dark:text-stone-300'}`}>
                    {g.mandatory ? 'Required' : 'Recommended'}
                  </span>
                  <span><strong>{g.title}</strong>{g.description ? ` — ${g.description}` : ''}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex gap-3">
            <button type="button" disabled={busy} onClick={validateRepository} className="rounded-lg bg-stone-700 px-4 py-2 text-sm text-white hover:bg-stone-600 disabled:opacity-50 dark:bg-stone-500 dark:hover:bg-stone-400">
              Validate Repository
            </button>
          </div>
          {validation && (
            <div className={`rounded-xl border p-4 ${validation.valid ? 'border-emerald-300 dark:border-emerald-800' : 'border-amber-300 dark:border-amber-800'} ${card}`}>
              <p className="font-semibold">{validation.valid ? 'Repository Valid' : 'Validation Gaps Detected'}</p>
              {validation.gaps && validation.gaps.length > 0 && (
                <ul className={`mt-2 list-disc pl-5 text-sm ${muted}`}>{validation.gaps.map((g) => <li key={g}>{g}</li>)}</ul>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'categories' && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((c) => (
            <div key={c.key} className={`rounded-xl border p-4 ${card}`}>
              <p className="font-semibold">{c.name}</p>
              {c.description && <p className={`mt-1 text-sm ${muted}`}>{c.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'template' && (
        <div className={`rounded-xl border ${card}`}>
          <table className="w-full text-sm">
            <thead><tr className={`border-b ${dark ? 'border-stone-800' : 'border-stone-200'}`}>
              <th className="p-3 text-left">Field</th><th className="p-3 text-left">Description</th><th className="p-3 text-left">Required</th>
            </tr></thead>
            <tbody>
              {template.map((f) => (
                <tr key={f.key} className={`border-b ${dark ? 'border-stone-800/50' : 'border-stone-100'}`}>
                  <td className="p-3 font-medium">{f.name}</td>
                  <td className={`p-3 ${muted}`}>{f.description}</td>
                  <td className="p-3">{f.required ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'mandatory' && (
        <div className="space-y-2">
          {mandatory.map((d) => (
            <div key={d.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{d.title}</span>
                <span className={`rounded px-2 py-0.5 text-xs ${statusColors[d.status] ?? statusColors.pending}`}>{d.status}</span>
                <span className={`text-xs ${muted}`}>{d.category}</span>
              </div>
              {d.description && <p className={`mt-1 text-sm ${muted}`}>{d.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'records' && (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="space-y-2 lg:col-span-1">
            {records.map((r) => (
              <button key={r.key} type="button" onClick={() => setSelectedRecord(r)}
                className={`w-full rounded-xl border p-3 text-left text-sm ${selectedRecord?.key === r.key ? 'border-stone-500 bg-stone-100 dark:border-stone-400 dark:bg-stone-800' : card}`}>
                <p className="font-medium">{r.id}: {r.title}</p>
                <p className={`text-xs ${muted}`}>{r.category} · {r.date}</p>
              </button>
            ))}
          </div>
          {selectedRecord && (
            <div className={`space-y-4 rounded-xl border p-5 lg:col-span-2 ${card}`}>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-lg font-semibold">{selectedRecord.id}: {selectedRecord.title}</h3>
                <span className={`rounded px-2 py-0.5 text-xs ${statusColors[selectedRecord.status] ?? statusColors.proposed}`}>{selectedRecord.status}</span>
              </div>
              {[
                { label: 'Context', value: selectedRecord.context },
                { label: 'Problem', value: selectedRecord.problem },
                { label: 'Decision', value: selectedRecord.decision },
              ].map((s) => (
                <div key={s.label}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">{s.label}</p>
                  <p className="mt-1 text-sm">{s.value}</p>
                </div>
              ))}
              {selectedRecord.alternatives && selectedRecord.alternatives.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Alternatives</p>
                  <ul className={`mt-1 list-disc pl-5 text-sm ${muted}`}>{selectedRecord.alternatives.map((a) => <li key={a}>{a}</li>)}</ul>
                </div>
              )}
              <div className="grid gap-4 sm:grid-cols-2">
                {selectedRecord.pros && selectedRecord.pros.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">Pros</p>
                    <ul className="mt-1 list-disc pl-5 text-sm">{selectedRecord.pros.map((p) => <li key={p}>{p}</li>)}</ul>
                  </div>
                )}
                {selectedRecord.cons && selectedRecord.cons.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">Cons</p>
                    <ul className="mt-1 list-disc pl-5 text-sm">{selectedRecord.cons.map((c) => <li key={c}>{c}</li>)}</ul>
                  </div>
                )}
              </div>
              {selectedRecord.risks && selectedRecord.risks.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-red-600">Risks & Mitigations</p>
                  <ul className="mt-1 space-y-1 text-sm">
                    {selectedRecord.risks.map((risk, i) => (
                      <li key={risk}>{risk}{selectedRecord.mitigations?.[i] ? ` → ${selectedRecord.mitigations[i]}` : ''}</li>
                    ))}
                  </ul>
                </div>
              )}
              {selectedRecord.related_adrs && selectedRecord.related_adrs.length > 0 && (
                <p className={`text-sm ${muted}`}>Related ADRs: {selectedRecord.related_adrs.join(', ')}</p>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'index' && (
        <div className={`rounded-xl border ${card}`}>
          <table className="w-full text-sm">
            <thead><tr className={`border-b ${dark ? 'border-stone-800' : 'border-stone-200'}`}>
              <th className="p-3 text-left">ID</th><th className="p-3 text-left">Title</th><th className="p-3 text-left">Category</th><th className="p-3 text-left">Status</th><th className="p-3 text-left">Date</th>
            </tr></thead>
            <tbody>
              {index.map((e) => (
                <tr key={e.key} className={`border-b ${dark ? 'border-stone-800/50' : 'border-stone-100'}`}>
                  <td className="p-3 font-mono text-xs">{e.adr_id}</td>
                  <td className="p-3">{e.title}</td>
                  <td className={`p-3 ${muted}`}>{e.category}</td>
                  <td className="p-3"><span className={`rounded px-2 py-0.5 text-xs ${statusColors[e.status] ?? statusColors.pending}`}>{e.status}</span></td>
                  <td className={`p-3 ${muted}`}>{e.date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'dependencies' && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <h3 className="font-semibold">Dependency Map</h3>
            <div className="mt-3 space-y-2">
              {dependencies.map((d) => (
                <div key={d.key} className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-mono text-xs">{d.from_id}</span>
                  <span className={muted}>→</span>
                  <span className="font-mono text-xs">{d.to_id}</span>
                  <span className={`rounded px-2 py-0.5 text-xs bg-stone-200 dark:bg-stone-700`}>{d.link_type}</span>
                  {d.label && <span className={muted}>{d.label}</span>}
                </div>
              ))}
            </div>
          </div>
          <div className={`rounded-xl border p-5 ${card}`}>
            <h3 className="font-semibold">Cross References</h3>
            <div className="mt-3 space-y-2">
              {crossRefs.map((r) => (
                <div key={r.key} className="text-sm">
                  <span className="font-mono text-xs">{r.source_id}</span>
                  <span className={muted}> {r.relationship} </span>
                  <span>{r.target_doc}</span>
                  <span className={`ml-2 text-xs ${muted}`}>({r.target_type})</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'governance' && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <h3 className="font-semibold">Governance Rules (§6)</h3>
            <ul className="mt-3 space-y-3">
              {governance.map((r) => (
                <li key={r.key} className="text-sm">
                  <span className={`mr-2 rounded px-2 py-0.5 text-xs ${r.enforced ? 'bg-stone-700 text-white dark:bg-stone-500' : 'bg-stone-200 dark:bg-stone-700'}`}>
                    {r.enforced ? 'Enforced' : 'Advisory'}
                  </span>
                  <strong>{r.rule}</strong>
                  {r.description && <p className={`mt-1 ${muted}`}>{r.description}</p>}
                </li>
              ))}
            </ul>
          </div>
          <div className={`rounded-xl border p-5 ${card}`}>
            <h3 className="font-semibold">Deliverables (§7)</h3>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {deliverables.map((d) => (
                <div key={d.key} className="rounded-lg border p-3 text-sm dark:border-stone-700">
                  <p className="font-medium">{d.name}</p>
                  {d.description && <p className={`mt-1 ${muted}`}>{d.description}</p>}
                  <span className={`mt-2 inline-block rounded px-2 py-0.5 text-xs ${statusColors[d.status] ?? statusColors.registered}`}>{d.status}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'status' && status && (
        <div className={`rounded-xl border p-5 ${card}`}>
          <h3 className="font-semibold">Repository Status</h3>
          <dl className="mt-4 grid gap-3 sm:grid-cols-2">
            {Object.entries(status).filter(([k]) => !['applies_to'].includes(k)).map(([k, v]) => (
              <div key={k}>
                <dt className={`text-xs uppercase ${muted}`}>{k.replace(/_/g, ' ')}</dt>
                <dd className="mt-0.5 font-medium">{typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)}</dd>
              </div>
            ))}
          </dl>
          <p className={`mt-4 text-sm ${muted}`}>Applies to: {status.applies_to.join(' · ')}</p>
        </div>
      )}
    </div>
  );
}
